import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Share2, FileText, FileDown, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from 'sonner'
import { exportAsHtml, exportAsPdf } from '@/lib/utils/draft-export'
import { draftQueries } from '@/lib/api/queries/draft'
import type { BlogDraft } from '@/lib/api/types'

interface ShareDraftButtonProps {
  draft: BlogDraft
  agentId: string
  draftId: string
}

export function ShareDraftButton({ draft, agentId, draftId }: ShareDraftButtonProps) {
  const [isExporting, setIsExporting] = useState(false)
  const { data: candidatesData } = useQuery(draftQueries.candidates(agentId, draftId))

  const getExportData = () => {
    const candidates = candidatesData?.data ?? []
    const selected = candidates.find((c) => c.selected)
    const hasHero = draft.heroImageUrl || draft.selectedImageUrl

    // If draft already has a hero image, use it; no extra candidates needed
    if (hasHero) return { draft, candidateImageUrls: [] }

    // If a candidate is selected, use it as hero
    if (selected) {
      return {
        draft: { ...draft, heroImageUrl: selected.imageUrl },
        candidateImageUrls: [],
      }
    }

    // No selection — include all candidate images in export
    return {
      draft,
      candidateImageUrls: candidates.map((c) => c.imageUrl),
    }
  }

  const handleHtmlExport = async () => {
    setIsExporting(true)
    try {
      const { draft: d, candidateImageUrls } = getExportData()
      await exportAsHtml(d, candidateImageUrls)
      toast.success('HTML file downloaded')
    } catch {
      toast.error('Failed to export as HTML')
    } finally {
      setIsExporting(false)
    }
  }

  const handlePdfExport = async () => {
    setIsExporting(true)
    try {
      const { draft: d, candidateImageUrls } = getExportData()
      await exportAsPdf(d, candidateImageUrls)
      toast.success('PDF file downloaded')
    } catch {
      toast.error('Failed to export as PDF')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={isExporting}>
          {isExporting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Share2 className="mr-2 h-4 w-4" />
          )}
          Share
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleHtmlExport}>
          <FileText className="mr-2 h-4 w-4" />
          Export as HTML
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handlePdfExport}>
          <FileDown className="mr-2 h-4 w-4" />
          Export as PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
